import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import ProtectedRouteV2 from './ProtectedRouteV2';
import FeatureProtectedRoute from './FeatureProtectedRoute';
import { SYSTEM_FEATURE } from '../types/system/features';

import RestaurantDashboard from '../pages/Restaurant/RestaurantDashboard';

import RestaurantDiningRooms from '../pages/Restaurant/RestaurantDiningRooms';
import RestaurantTables from '../pages/Restaurant/RestaurantTables';
import RestaurantReservations from '../pages/Restaurant/RestaurantReservations';
import RestaurantOrders from '../pages/Restaurant/RestaurantOrders';
import RestaurantKitchen from '../pages/Restaurant/RestaurantKitchen';

import RestaurantMenuCategories from '../pages/Restaurant/RestaurantMenuCategories';
import RestaurantMenu from '../pages/Restaurant/RestaurantMenu';
import RestaurantMenuConfiguration from '../pages/Restaurant/RestaurantMenuConfiguration';
import RestaurantMenuPrepTimes from '../pages/Restaurant/RestaurantMenuPrepTimes';
import RestaurantRecipes from '../pages/Restaurant/RestaurantRecipes';

import RestaurantRawMaterialCategories from '../pages/Restaurant/RestaurantRawMaterialCategories';
import RestaurantRawMaterials from '../pages/Restaurant/RestaurantRawMaterials';
import RestaurantPurchases from '../pages/Restaurant/RestaurantPurchases';
import RestaurantInventory from '../pages/Restaurant/RestaurantInventory';
import RestaurantInventoryMovements from '../pages/Restaurant/RestaurantInventoryMovements';
import RestaurantWaste from '../pages/Restaurant/RestaurantWaste';
import RestaurantUnitConversions from '../pages/Restaurant/RestaurantUnitConversions';

import RestaurantProduction from '../pages/Restaurant/RestaurantProduction';

import RestaurantSettings from '../pages/Restaurant/RestaurantSettings';
import RestaurantPrinters from '../pages/Restaurant/RestaurantPrinters';
import RestaurantPreparationStations from '../pages/Restaurant/RestaurantPreparationStations';

import RestaurantReports from '../pages/Restaurant/RestaurantReports';
import RestaurantCorporateConsumptionPanel from '../pages/Restaurant/RestaurantCorporateConsumptionPanel';
import RestaurantCorporateConsumptionHistory from '../pages/Restaurant/RestaurantCorporateConsumptionHistory';
import RestaurantCorporateAccounts from '../pages/Restaurant/RestaurantCorporateAccounts';
import RestaurantCorporateSettlements from '../pages/Restaurant/RestaurantCorporateSettlements';

const withTitle = (title: string, Component: React.ComponentType) => (
  <>
    <PageTitle title={`${title} | Codenova POS`} />
    <Component />
  </>
);

const restaurantRoutes = (
  <Route element={<ProtectedRouteV2 />}>
    <Route
      element={<FeatureProtectedRoute feature={SYSTEM_FEATURE.RESTAURANT} />}
    >
    <Route
      path="/restaurant"
      element={<Navigate to="/restaurant/dashboard" replace />}
    />

    {/* Dashboard */}
    <Route
      path="/restaurant/dashboard"
      element={withTitle('Dashboard Restaurante', RestaurantDashboard)}
    />

    {/* Operación */}
    <Route
      path="/restaurant/operation/dining-rooms"
      element={withTitle('Salones', RestaurantDiningRooms)}
    />
    <Route
      path="/restaurant/operation/tables"
      element={withTitle('Mesas', RestaurantTables)}
    />
    <Route
      path="/restaurant/operation/reservations"
      element={withTitle('Reservaciones', RestaurantReservations)}
    />
    <Route
      path="/restaurant/operation/orders"
      element={withTitle('Órdenes', RestaurantOrders)}
    />
    <Route
      path="/restaurant/operation/kitchen"
      element={withTitle('Cocina', RestaurantKitchen)}
    />

    {/* Menú */}
    <Route
      path="/restaurant/menu/categories"
      element={withTitle('Categorías del Menú', RestaurantMenuCategories)}
    />
    <Route
      path="/restaurant/menu/items"
      element={withTitle('Menú', RestaurantMenu)}
    />
    <Route
      path="/restaurant/menu/preparation-times"
      element={withTitle('Tiempos de preparación', RestaurantMenuPrepTimes)}
    />
    <Route
      path="/restaurant/menu/recipes"
      element={withTitle('Recetas', RestaurantRecipes)}
    />

    {/* Inventario */}
    <Route
      path="/restaurant/inventory/categories"
      element={withTitle(
        'Categorías Materia Prima',
        RestaurantRawMaterialCategories
      )}
    />
    <Route
      path="/restaurant/inventory/raw-materials"
      element={withTitle('Materia Prima', RestaurantRawMaterials)}
    />
    <Route
      path="/restaurant/inventory/purchases"
      element={withTitle('Compras Materia Prima', RestaurantPurchases)}
    />
    <Route
      path="/restaurant/inventory/stock"
      element={withTitle('Inventario Materia Prima', RestaurantInventory)}
    />
    <Route
      path="/restaurant/inventory/movements"
      element={withTitle(
        'Movimientos de Inventario',
        RestaurantInventoryMovements
      )}
    />
    <Route
      path="/restaurant/menu/configuration"
      element={withTitle('Configuración del Menú', RestaurantMenuConfiguration)}
    />
    <Route
      path="/restaurant/inventory/waste"
      element={withTitle('Merma', RestaurantWaste)}
    />
    <Route
      path="/restaurant/inventory/unit-conversions"
      element={withTitle('Conversiones de Unidad', RestaurantUnitConversions)}
    />

    {/* Producción */}
    <Route
      path="/restaurant/production"
      element={withTitle('Producción', RestaurantProduction)}
    />

    {/* Configuración */}
    <Route
      path="/restaurant/settings"
      element={withTitle('Configuración Restaurante', RestaurantSettings)}
    />
    <Route
      path="/restaurant/settings/printers"
      element={withTitle('Impresoras del Restaurante', RestaurantPrinters)}
    />
    <Route
      path="/restaurant/settings/preparation-stations"
      element={withTitle(
        'Estaciones de Preparación',
        RestaurantPreparationStations
      )}
    />

    {/* Reportes */}
    <Route
      path="/restaurant/reports"
      element={withTitle('Reportes Restaurante', RestaurantReports)}
    />

    {/* Cafetería corporativa */}
    <Route
      path="/restaurant/corporate-cafeteria"
      element={
        <Navigate
          to="/restaurant/corporate-cafeteria/consumptions/new"
          replace
        />
      }
    />
    <Route
      path="/restaurant/corporate-cafeteria/consumptions/new"
      element={withTitle(
        'Consumo corporativo',
        RestaurantCorporateConsumptionPanel
      )}
    />
    <Route
      path="/restaurant/corporate-cafeteria/consumptions/:id/edit"
      element={withTitle(
        'Editar consumo corporativo',
        RestaurantCorporateConsumptionPanel
      )}
    />
    <Route
      path="/restaurant/corporate-cafeteria/consumptions"
      element={withTitle(
        'Historial corporativo',
        RestaurantCorporateConsumptionHistory
      )}
    />
    <Route
      path="/restaurant/corporate-cafeteria/accounts"
      element={withTitle('Cuentas corporativas', RestaurantCorporateAccounts)}
    />
    <Route
      path="/restaurant/corporate-cafeteria/settlements"
      element={withTitle(
        'Liquidaciones corporativas',
        RestaurantCorporateSettlements
      )}
    />
    </Route>
  </Route>
);

export default restaurantRoutes;
